import { GraphConfiguration } from "@/type/common";
import { DomainRule } from "../Domain";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, XCircle } from "lucide-react";
import useSearch from "@/hooks/use-search";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import GraphConfigContext from "@/store/contexts/GraphConfigContext";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";

const ManyToManyPicker = ({
  config,
  rule,
  onChange,
}: {
  config: GraphConfiguration | null;
  rule: DomainRule;
  onChange: (value: any) => void;
}) => {
  const { fields } = useContext(GraphConfigContext);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const timeout = useRef<NodeJS.Timeout | null>(null);

  // Parse current value as array of IDs
  const selectedIds = useMemo(() => {
    if (!rule.value) return [];
    if (Array.isArray(rule.value)) return rule.value;
    if (typeof rule.value === "string") {
      try {
        // Try parsing as JSON array
        const parsed = JSON.parse(rule.value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        // Try parsing as comma-separated values
        return rule.value
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v !== "");
      }
    }
    return [];
  }, [rule.value]);

  const model = useMemo(() => {
    return (
      rule.fieldObject?.model ||
      fields.find((f: any) => f.field === rule.field)?.model
    );
  }, [fields, rule.field, rule.fieldObject]);

  const {
    data: records,
    search: searchRecords,
    updateUrl,
  } = useSearch(`/get/model_search/${model}`, "");

  useEffect(() => {
    updateUrl(`/get/model_search/${model}`);
  }, [model]);

  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      searchRecords(search);
    }, 500);
  }, [search]);

  // Get selected record names for display
  const selectedRecords = useMemo(() => {
    if (!records) return [];
    return records.filter((r: any) => selectedIds.includes(r.id));
  }, [records, selectedIds]);

  const toggleRecord = (recordId: number) => {
    const newIds = selectedIds.includes(recordId)
      ? selectedIds.filter((id) => id !== recordId)
      : [...selectedIds, recordId];
    onChange(newIds);
  };

  const removeRecord = (recordId: number) => {
    const newIds = selectedIds.filter((id) => id !== recordId);
    onChange(newIds);
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit"
          )}
        >
          {selectedRecords.length > 0 ? (
            <div className="flex justify-between items-center w-full">
              <div className="flex flex-wrap items-center gap-1">
                {selectedRecords.slice(0, 3).map((record: any) => (
                  <Badge key={record.id} variant="secondary" className="m-1">
                    {record.name}
                    <div
                      onClick={(event) => {
                        event.stopPropagation();
                        removeRecord(record.id);
                      }}
                    >
                      <XCircle className="ml-2 h-3 w-3 cursor-pointer" />
                    </div>
                  </Badge>
                ))}
                {selectedRecords.length > 3 && (
                  <Badge variant="secondary" className="m-1">
                    +{selectedRecords.length - 3} more
                  </Badge>
                )}
              </div>
              <div className="flex items-center">
                {selectedRecords.length > 0 && (
                  <div
                    onClick={(event) => {
                      event.stopPropagation();
                      clearAll();
                    }}
                  >
                    <XCircle className="h-4 w-4 mx-2 cursor-pointer text-muted-foreground" />
                  </div>
                )}
                <ChevronsUpDown className="h-4 w-4 opacity-50" />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-muted-foreground mx-3">
                Select records...
              </span>
              <ChevronsUpDown className="h-4 w-4 opacity-50 mx-2" />
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 pb-1 w-[400px]" align="start">
        <div className="mb-2 border-b">
          <Input
            placeholder="Search..."
            className="border-none focus-visible:border-none focus-visible:ring-0 shadow-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ScrollArea className="h-[250px] px-1">
          <div className="flex flex-col">
            {records?.map((record: any) => {
              const isSelected = selectedIds.includes(record.id);
              return (
                <div
                  key={record.id}
                  className="flex items-center cursor-pointer hover:bg-accent rounded-sm p-2 gap-2"
                  onClick={() => toggleRecord(record.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleRecord(record.id)}
                  />
                  <span className="text-sm flex-1">{record.name}</span>
                </div>
              );
            })}
            {records?.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4">
                No records found
              </div>
            )}
          </div>
        </ScrollArea>
        <div className="border-t pt-2 px-2 flex justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            disabled={selectedRecords.length === 0}
          >
            Clear all
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            Close
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ManyToManyPicker;
